/**
 * The data the M6 station panels read (plan P27): the player-buyable hulls (hulls.json entries with a `class`) with
 * their class row (fleet.json), ship-def name (ships.json) and insured value; the ware base prices and market curve
 * (wares.json); the insurance and transfer rules (fleet.json); the panel config (panels.json). Everything is parsed
 * once with zod and frozen. Pure.
 *
 * M10 P5a1a (plan 2026-10-17-m10-p5a §3.1, hull tiers spec §6.3): each offer also carries what the hull is: its band,
 * length (the row's `lengthM`, else the def's asset bounds length along Z, as `shipResolve` bands it), maker,
 * role (its hull-lines line's role, a variant's own role), dock mode (`bands.json`) and the maker's doctrine line.
 */
import { z } from 'zod'
import fleetJson from '../../../data/strategic/fleet.json'
import hullsJson from '../../../data/strategic/hulls.json'
import waresJson from '../../../data/strategic/wares.json'
import assetsJson from '../../../data/assets.json'
import { dockModeOf, type DockMode } from '../../../data/bands'
import { mergedShipsRaw } from '../../../data/shipExtends'
import { HULL_BAND_IDS, HULL_ROLE_IDS, loadHullLines, type HullBand, type HullLines, type HullRoleId } from '../../../strategic/hullLines'
import { fleetConfigSchema, hullsFileSchema, waresFileSchema, type ShipClassDef } from '../../../strategic/schemas'
import { SHIP_CLASS_NAMES, SKILL_NAMES, type ShipClassName, type SkillName } from '../../../strategic/types'
import panelsJson from '../panels.json'
import { shipyardRules, yardBuilds, type ShipyardRules } from './gates'

/** A role's shop text: the glyph (a text placeholder until the art, D-052) and its label. */
const roleTextSchema = z.strictObject({ glyph: z.string().min(1), label: z.string().min(1) })

const panelsSchema = z.strictObject({
  comment: z.string(),
  order: z.strictObject({ shipyard: z.number(), hangar: z.number(), roster: z.number() }),
  /**
   * H10: the roster's station tab label and heading ("Hire captains", "Hiring board"). CL-7: the Hangar's leased-hull
   * row (while the lease isn't owned) and its empty line.
   */
  labels: z.strictObject({
    hangar: z.strictObject({ leasedHeader: z.string().min(1), leased: z.string().min(1), empty: z.string().min(1) }),
    roster: z.strictObject({ tab: z.string().min(1), title: z.string().min(1) }),
  }),
  classLabels: z.strictObject(Object.fromEntries(SHIP_CLASS_NAMES.map((c) => [c, z.string().min(1)])) as Record<ShipClassName, z.ZodString>),
  skillShort: z.strictObject(Object.fromEntries(SKILL_NAMES.map((s) => [s, z.string().min(1)])) as Record<SkillName, z.ZodString>),
  /** M10 P5a1a: the shop strings (band tab labels, the dock-mode row line, role glyphs and labels). */
  shipyard: z.strictObject({
    tabs: z.strictObject(Object.fromEntries(HULL_BAND_IDS.map((b) => [b, z.string().min(1)])) as Record<HullBand, z.ZodString>),
    dockLines: z.partialRecord(z.enum(['bay', 'moor', 'station']), z.string().min(1)),
    roles: z.strictObject(Object.fromEntries(HULL_ROLE_IDS.map((r) => [r, roleTextSchema])) as Record<HullRoleId, typeof roleTextSchema>),
    lengthUnit: z.string().min(1),
  }),
})
export type PanelsConfig = z.infer<typeof panelsSchema>


/** One hull a shipyard sells to the player. */
export interface HullOffer {
  id: string
  shipDef: string
  /** The ship def's name ("Corvette"). */
  name: string
  shipClass: ShipClassName
  classLabel: string
  cls: ShipClassDef
  cargo: number
  crew: number
  /** Standing with the yard's faction needed to buy (null = none). */
  standingReq: number | null
  /** Component tonnes by ware id. */
  buildCost: Readonly<Record<string, number>>
  /** Σ buildCost × base price: the insured value (the worker's `hullValue`). */
  value: number
  /** M10 P5a1a: the size band (the row's stamped band). */
  band: HullBand
  /** The hull length in metres: the row's `lengthM`, else the def's asset bounds length (null when neither reads). */
  lengthM: number | null
  /** The maker's display name ("Tethys"), or null on an unlined hull. */
  maker: string | null
  /** The hull role (its line's, a variant's own), or null when none is set (a story line). */
  role: HullRoleId | null
  /** How the band docks (`bands.json dockMode`): bay, moor (L) or station (XL). */
  dockMode: DockMode
  /** The maker's shipyard doctrine string (`hull-lines.json makers.<id>.line`), or null (an operator, unlined). */
  doctrine: string | null
}

export interface MarketCurve {
  minMult: number
  maxMult: number
}

export interface PanelCatalog {
  /** The hulls a shipyard offers (M10 P4a6: not `sale: false`, not story; M10 P4b1a1: not retired). */
  hulls: readonly HullOffer[]
  /** Every hull with a class, by id (an owned ship's row, sold or not). */
  hullById: ReadonlyMap<string, HullOffer>
  basePrice: ReadonlyMap<string, number>
  market: MarketCurve
  priceMargin: number
  insurance: { requiresLeaseOwned: boolean; dailyRate: number; minPerDay: number }
  transfer: { feePerJump: number; minFee: number }
  config: PanelsConfig
}

/** The asset bounds the length readout reads (the rest of each `assets.json` model is `data/index.ts`'s to parse). */
const vec3 = z.tuple([z.number(), z.number(), z.number()])
const assetBoundsSchema = z.object({ models: z.array(z.object({ id: z.string(), bounds: z.object({ min: vec3, max: vec3 }) })) })

/** Asset id → bounds length along Z (m), the long axis `shipResolve` bands a hull by. */
function assetLengths(raw: unknown): Map<string, number> {
  return new Map(assetBoundsSchema.parse(raw).models.map((m) => [m.id, m.bounds.max[2] - m.bounds.min[2]]))
}

/** Hull id → role from `hull-lines.json`: a line hull takes its line's role, a variant its own or its base's. */
function rolesOf(lines: HullLines): Map<string, HullRoleId> {
  const role = new Map<string, HullRoleId>()
  for (const l of lines.lines) if (l.role !== undefined) for (const h of l.hulls) role.set(h.id, l.role)
  for (const v of lines.variants) {
    const r = v.role ?? role.get(v.base)
    if (r !== undefined) role.set(v.id, r)
  }
  return role
}

/** Builds the catalogue from a raw `hulls.json` (default: the shipped one; tests pass a copy; `linesRaw` likewise). */
export function buildPanelCatalog(hullsRaw: unknown = hullsJson, linesRaw?: unknown): PanelCatalog {
  const hulls = hullsFileSchema.parse(hullsRaw)
  const fleet = fleetConfigSchema.parse(fleetJson)
  const wares = waresFileSchema.parse(waresJson)
  const config = panelsSchema.parse(panelsJson)
  // M10 P1d: the merged list, so an `extends` entry's inherited name reads.
  const merged = mergedShipsRaw()
  const names = new Map(merged.map((s) => [s.id, s.name]))
  const lengths = assetLengths(assetsJson)
  const assetLength = new Map(
    merged.flatMap((s) => {
      const len = lengths.get(s.assetId)
      return len === undefined ? [] : [[s.id, len] as const]
    }),
  )
  const lines = loadHullLines(linesRaw)
  const roles = rolesOf(lines)
  const basePrice = new Map(wares.wares.map((w) => [w.id as string, w.basePrice]))
  const offers: HullOffer[] = []
  const all: HullOffer[] = []
  for (const h of hulls.hulls) {
    const c = h.class
    if (c === undefined) continue
    const cls = fleet.classes[c]
    if (cls === undefined) continue
    let value = 0
    const cost: Record<string, number> = {}
    for (const [w, q] of Object.entries(h.buildCost)) {
      if (q === undefined) continue
      cost[w] = q
      value += q * (basePrice.get(w) ?? 0)
    }
    if (h.band === undefined) throw new Error(`hulls.json: hull ${h.id} has a class but no band`)
    const maker = h.maker === undefined ? undefined : lines.makers[h.maker]
    const offer: HullOffer = Object.freeze({
      id: h.id,
      shipDef: h.shipDef,
      name: names.get(h.shipDef) ?? h.id,
      shipClass: c,
      classLabel: config.classLabels[c],
      cls,
      cargo: h.cargo,
      crew: h.crew ?? cls.crew[0],
      standingReq: h.standingReq ?? null,
      buildCost: Object.freeze(cost),
      value,
      band: h.band,
      lengthM: h.lengthM ?? assetLength.get(h.shipDef) ?? null,
      maker: maker?.name ?? null,
      role: roles.get(h.id) ?? null,
      dockMode: dockModeOf(h.band),
      doctrine: maker?.line ?? null,
    })
    all.push(offer)
    // M10 P4a6 (plan 2026-10-11-m10-p4a §0.3): a hull stamped `sale: false` (the cutter, the courier) or a story hull is
    // never offered, though an owned one (a dev grant) still reads its row by id; P5a1 replaces this with the catalogue
    // rows and the device rule. M10 P4b1a1: a retired row (no longer built) is never offered either.
    if (h.sale !== false && h.story !== true && h.retired !== true) offers.push(offer)
  }
  return Object.freeze({
    hulls: Object.freeze(offers),
    hullById: new Map(all.map((o) => [o.id, o])),
    basePrice,
    market: { minMult: wares.market.minMult, maxMult: wares.market.maxMult },
    priceMargin: hulls.priceMargin,
    insurance: { ...fleet.insurance },
    transfer: {
      feePerJump: fleet.transfer.feePerJump,
      minFee: fleet.transfer.minFee,
    },
    config,
  })
}

/**
 * M9 (CH-8, H-6): the insured value of a ship of hull `offer` with value factor `valueMult` (the worker's
 * `insuredValue`: hull value × `valueMult`). A new ship is pristine (1), so its insured value is exactly `value`.
 */
export function insuredValueOf(offer: HullOffer, valueMult = 1): number {
  return offer.value * valueMult
}

let cached: PanelCatalog | null = null

/** The catalogue from the shipped data (parsed once). */
export function panelCatalog(): PanelCatalog {
  cached ??= buildPanelCatalog()
  return cached
}

/**
 * M10 P5a1b2 (plan 2026-10-17-m10-p5a §3.2 P5a1b, hull tiers spec §6.3: "gone is every yard sells every hull"): the
 * hulls a yard with tooling faction `tooling` offers, from the yard's catalogue: an export pattern, the tooling's own
 * line, or a line it holds a licence for (`yardBuilds`, the main-thread copy of the worker's `toolingBuilds`), among
 * the sold rows (`cat.hulls`). The yard's tooling is its builder (`stations.tooling`); the station view carries only
 * the owner, so the caller passes the owner until a view field lands (a capture changes the owner, not the tooling).
 * Behind `shipyard.json catalogueRows` (false until W6): off, this is `cat.hulls` itself (today's rows).
 */
export function yardOffers(cat: PanelCatalog, tooling: string | null, rules: ShipyardRules = shipyardRules(), lines?: HullLines): readonly HullOffer[] {
  if (!rules.catalogueRows) return cat.hulls
  return Object.freeze(cat.hulls.filter((h) => yardBuilds(tooling, h.id, lines)))
}
